import * as moment from "moment";
import { FormEvent, useMemo, useState } from 'react';
import { ISerologyTracking, SerologyTrackingListType } from '@nkr/models/index';
import { SerologyListType } from '@nkr/store/index';
import { CellClickedEvent, ColDef, FirstDataRenderedEvent, GridReadyEvent, GridSizeChangedEvent, ICellRendererParams, IHeaderParams, RowClassParams, RowDataChangedEvent, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { FormControl } from 'react-bootstrap';
import { GridWrapper } from './styles';
import { Utility } from '@nkr/services/utility';
import SortableHeaderComponent from '../admin/center/list/common/sortable-header';
import ActionsDropdownAdmin from './actions-dropdown-admin';

interface IActionParams extends ICellRendererParams { onItemSelected: (data: ISerologyTracking, action: number) => void };

interface IProps {
    data: ISerologyTracking[],
    centerId: number,
    listType?: SerologyListType,
    adminListType?: SerologyTrackingListType,
    isAdmin: boolean,
    onView: (id: number) => void,
    onAction: (data: ISerologyTracking, action: number) => void,
    onSerologyTrackingDetails: (id: number) => void,
    onUpdateDeadline: (id: number, deadline: string) => void
}

const Grid = (props: IProps) => {
    const [menuDisplayed, setMenuDisplayed] = useState(false);

    const onGridReady = (e: GridReadyEvent) => {
        e.api.sizeColumnsToFit()
    }

    const onDataFirstRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    }

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    }

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onSortChanged = (e: SortChangedEvent) => {
        e.api.refreshCells();
    };

    const handleMenuToggle = (show: boolean) => {
        setMenuDisplayed(show);
    };

    const handleAction = (data: ISerologyTracking, action: number) => {
        props.onAction(data, action);
    }

    const handleUpdateDeadline = (e: FormEvent<FormControl & HTMLInputElement>) => {
        let { id, value } = e.currentTarget;
        let serologyTrackingId = parseInt(id);

        props.onUpdateDeadline(serologyTrackingId, value);
    };

    let columns: ColDef[] = [
        {
            colId: 'rowNumber',
            headerName: 'R#',
            width: 50,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>R#</SortableHeaderComponent>,
            valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1
        },
        {
            colId: 'id',
            field: 'id',
            headerName: 'Order #',
            width: 75,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Order #</SortableHeaderComponent>,
        },
        {
            colId: 'donorCenter',
            field: 'donorCenter',
            headerName: 'Donor Center',
            width: 150,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor Center</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ISerologyTracking;

                if (data.rdnCenterId > 0) {
                    return <>{data.donorCenter} (<span className='remote-center'>{data.rdnCenter}</span>)</>
                }

                return <>{data.donorCenter}</>
            }
        },
        {
            colId: 'donorAlias',
            field: 'donorAlias',
            headerName: 'Donor Alias',
            width: 150,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor Alias</SortableHeaderComponent>,
        },
        {
            colId: 'recipientCenter',
            field: 'recipientCenter',
            headerName: 'Recipient Center',
            width: 150,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient Center</SortableHeaderComponent>,
        },
        {
            colId: 'recipientAlias',
            field: 'recipientAlias',
            headerName: 'Recipient Alias',
            width: 200,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient Alias</SortableHeaderComponent>,
        },
        {
            colId: 'chainNumber',
            field: 'chainNumber',
            headerName: 'Swap #',
            width: 200,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Swap #</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ISerologyTracking;
                return <>{data.chainNumber}/{data.clusterNumber}/{data.positionNumber}</>
            }
        },
        {
            colId: 'targetSurgeryDate',
            field: 'targetSurgeryDate',
            headerName: 'Target Surgery Date',
            width: 150,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Target Surgery Date</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ISerologyTracking;

                if (!data.targetSurgeryDate || data.targetSurgeryDate.length <= 4) {
                    return <>--</>
                }

                return <>{data.targetSurgeryDate}</>
            },
            comparator: Utility.dateComparator
        },
        {
            colId: 'requestedDate',
            field: 'requestedDate',
            headerName: 'Date Requested',
            width: 150,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Date Requested</SortableHeaderComponent>,
            comparator: Utility.dateComparator
        },
        {
            colId: 'statusDeadline',
            field: 'statusDeadline',
            headerName: 'Deadline',
            width: 160,
            hide: props.adminListType != SerologyTrackingListType.ShippedToDonorCenter && props.adminListType != SerologyTrackingListType.ShippedBackToVendor,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Deadline</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ISerologyTracking;

                if (props.adminListType == SerologyTrackingListType.ShippedToDonorCenter || props.adminListType == SerologyTrackingListType.ShippedBackToVendor) {
                    return (
                        <FormControl
                            id={data.id.toString()}
                            type='date'
                            bsSize='xs'
                            value={moment(data.statusDeadline, 'MM/DD/YYYY').format('YYYY-MM-DD')}
                            onChange={handleUpdateDeadline}
                        />
                    );
                }

                return <>{data.statusDeadline}</>
            },
            comparator: Utility.dateComparator
        },
        {
            colId: 'status',
            field: 'status',
            headerName: 'Status',
            width: 245,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Status</SortableHeaderComponent>,
        },
        {
            colId: 'actions',
            field: '',
            cellClass: 'action-menu',
            width: 100,
            hide: props.isAdmin == false || props.adminListType == SerologyTrackingListType.Completed || props.adminListType == SerologyTrackingListType.Cancelled,
            cellRendererParams: { onItemSelected: handleAction },
            cellRendererFramework: (params: IActionParams) => {
                return (
                    <ActionsDropdownAdmin
                        data={params.data}
                        centerId={props.centerId}
                        listType={props.adminListType}
                        onItemSelected={params.onItemSelected}
                        onMenuToggle={handleMenuToggle}
                    />
                )
            }
        }
    ];

    const columnDefs = useMemo(() => columns, [props.listType, props.adminListType]);
    const rowData = useMemo(() => props.data, [props.data]);

    return (
        <GridWrapper className='ag-theme-bootstrap' style={{ height: props.centerId > 0 ? '60vh' : '78vh' }}>
            <AgGridReact
                defaultColDef={{
                    headerClass: 'list-header',
                    cellClass: 'list-cell',
                    sortable: true
                }}
                columnDefs={columnDefs}
                rowData={rowData}
                headerHeight={30}
                rowHeight={32}
                noRowsOverlayComponentFramework={() => <div>No serology requests were found...</div>}
                suppressRowClickSelection={false}
                suppressCellSelection={true}
                enableCellTextSelection={true}
                suppressContextMenu={true}
                getRowNodeId={(data: ISerologyTracking) => data.id.toString()}
                getRowClass={(params: RowClassParams) => {
                    let data = params.data as ISerologyTracking;

                    if (props.adminListType != null) {
                        let plus5 = moment().addDays(5);
                        let plus3 = moment().addDays(3);

                        let tsd = moment(data.targetSurgeryDate, 'MM/DD/YYYY HH:mm');

                        let within3Days = plus3.isAfter(tsd)
                        let within5Days = plus5.isAfter(tsd);

                        if (within3Days) {
                            return 'within-3-days';
                        }

                        if (within5Days) {
                            return 'within-5-days';
                        }
                    }

                    return null;
                }}
                onGridReady={onGridReady}
                onFirstDataRendered={onDataFirstRendered}
                onRowDataChanged={onRowDataChanged}
                onBodyScroll={() => {
                    if (menuDisplayed) {
                        document.body.click();
                        setMenuDisplayed(false);
                    }
                }}
                onCellClicked={(e: CellClickedEvent) => {
                    if (e.colDef.field != 'id' && e.colDef.colId != 'actions' && e.colDef.colId != 'statusDeadline') {
                        let { donorId } = (e.data as ISerologyTracking);
                        props.onView(donorId);
                    }
                }}
                onGridSizeChanged={onGridSizeChanged}
                onSortChanged={onSortChanged}
            />
        </GridWrapper>
    );
}

export default Grid;